import { useCallback } from 'react';
import { toast } from 'sonner';
import { StoreCore } from '../types/store';
import { TaskTemplate } from '../types/family';
import { OccurrenceState } from '../types/activity';
import { DEMO_DATE } from '../data/demo';
import { copy } from '../data/copy';
import { DEFAULT_OCC, removeTx, upsertTx, withOcc } from '../utils/db';
import { equalShares, isDoneStatus } from '../utils/progress';
import { isGivenAway } from '../utils/today';

const pick = (list: string[]) => list[Math.floor(Math.random() * list.length)];

export function useTaskActions(core: StoreCore) {
  const { setDb, family, viewer, isAdmin, familyOcc } = core;
  const occOf = useCallback((id: string): OccurrenceState => familyOcc[id] ?? DEFAULT_OCC, [familyOcc]);
  const tpl = useCallback((id: string) => family.templates.find((t) => t.id === id), [family]);

  const canAct = useCallback(
    (t: TaskTemplate, o: OccurrenceState) =>
      (t.assigneeIds.includes(viewer.id) && !isGivenAway(o, viewer.id)) ||
      o.takenBy === viewer.id ||
      t.assigneeIds.length === 0,
    [viewer.id]
  );

  const txId = useCallback((tid: string, mid: string) => `occ:${family.id}:${tid}:${DEMO_DATE}:${mid}`, [family.id]);

  const checkTrust = useCallback(
    (id: string) => {
      const t = tpl(id);
      const o = occOf(id);
      if (!t || !canAct(t, o) || t.confirmation !== 'zaupanje' || isDoneStatus(o.status)) return;
      const outsider = !t.assigneeIds.includes(viewer.id);
      setDb((db) => {
        let next = withOcc(db, family.id, id, { status: 'opravljeno', sync: 'pending', performerId: viewer.id });
        if (outsider && t.baseValue) {
          next = upsertTx(next, {
            id: txId(id, viewer.id),
            familyId: family.id,
            memberId: viewer.id,
            amount: t.baseValue,
            reason: `${t.title} – ${t.assigneeIds.length > 0 ? 'prevzem' : 'skupna skrb'}`,
            date: DEMO_DATE,
          });
        }
        return next;
      });
      if (outsider) toast.success(t.group === 'zivali' ? copy.animalThanks : pick(copy.heroToasts));
    },
    [tpl, occOf, canAct, viewer.id, setDb, family.id, txId]
  );

  const uncheck = useCallback(
    (id: string) => {
      const o = occOf(id);
      if (o.status !== 'opravljeno' || (o.performerId && o.performerId !== viewer.id)) return;
      setDb((db) => removeTx(withOcc(db, family.id, id, { status: 'odprto', sync: 'pending', performerId: undefined }), txId(id, viewer.id)));
    },
    [occOf, viewer.id, setDb, family.id, txId]
  );

  const submitForReview = useCallback(
    (id: string) => {
      const t = tpl(id);
      const o = occOf(id);
      if (!t || !canAct(t, o) || t.confirmation !== 'skrbnik') return;
      if (o.status !== 'odprto' && o.status !== 'popravek') return;
      setDb((db) =>
        withOcc(db, family.id, id, {
          status: 'v_pregledu',
          sync: 'pending',
          submittedBy: viewer.id,
          submittedAt: Date.now(),
          correctionNote: undefined,
          performerId: t.assigneeIds.length === 0 ? viewer.id : o.performerId,
        })
      );
      toast.success(`${t.title}: oddano v pregled`);
    },
    [tpl, occOf, canAct, setDb, family.id, viewer.id]
  );

  /** Hitro dejanje pri skupnih nalogah brez stalnega nosilca. */
  const markShared = useCallback(
    (id: string) => {
      const t = tpl(id);
      if (!t) return;
      if (t.confirmation === 'skrbnik') submitForReview(id);
      else checkTrust(id);
    },
    [tpl, submitForReview, checkTrust]
  );

  const approve = useCallback(
    (id: string, shares: Record<string, number>) => {
      const t = tpl(id);
      const o = occOf(id);
      if (!t || !isAdmin || o.status === 'potrjeno') return;
      const defaults = equalShares(t.baseValue ?? 0, t.assigneeIds);
      setDb((db) => {
        let next = withOcc(db, family.id, id, { status: 'potrjeno', sync: 'pending', shares, reviewedBy: viewer.id });
        Object.entries(shares).forEach(([m, pts]) => {
          const base = t.assigneeIds.includes(m) && !isGivenAway(o, m) ? Math.min(pts, defaults[m] ?? 0) : 0;
          const bonus = pts - base;
          const key = `appr:${family.id}:${id}:${m}`;
          next = bonus > 0
            ? upsertTx(next, { id: key, familyId: family.id, memberId: m, amount: bonus, reason: `${t.title} – dodatni delež`, date: DEMO_DATE })
            : removeTx(next, key);
        });
        return next;
      });
      toast.success(`${t.title}: potrjeno`);
    },
    [tpl, occOf, isAdmin, setDb, family.id, viewer.id]
  );

  const requestCorrection = useCallback(
    (id: string, note: string) => {
      const st = occOf(id).status;
      if (!isAdmin || (st !== 'v_pregledu' && st !== 'odprto')) return;
      setDb((db) => withOcc(db, family.id, id, { status: 'popravek', sync: 'pending', correctionNote: note, reviewedBy: viewer.id }));
      toast(`Popravek poslan: ${tpl(id)?.title ?? 'opravilo'}`);
    },
    [isAdmin, occOf, setDb, family.id, viewer.id, tpl]
  );

  const offer = useCallback(
    (id: string, toId: string) => {
      const t = tpl(id);
      const o = occOf(id);
      if (!t || !t.transferable || !t.assigneeIds.includes(viewer.id) || isDoneStatus(o.status)) return;
      setDb((db) => withOcc(db, family.id, id, { offer: { byId: viewer.id, toId, at: Date.now() }, takenBy: undefined, sync: 'pending' }));
      toast.success('Ponujeno. Do sprejema je naloga še vedno tvoja.');
    },
    [tpl, occOf, viewer.id, setDb, family.id]
  );

  const cancelOffer = useCallback(
    (id: string) => {
      const o = occOf(id);
      if (!o.offer || o.offer.byId !== viewer.id || o.takenBy) return;
      setDb((db) => withOcc(db, family.id, id, { offer: undefined, sync: 'pending' }));
    },
    [occOf, viewer.id, setDb, family.id]
  );

  const takeOffer = useCallback(
    (id: string) => {
      const o = occOf(id);
      if (!o.offer || o.takenBy || o.offer.byId === viewer.id) return;
      if (o.offer.toId !== 'vsi' && o.offer.toId !== viewer.id) return;
      setDb((db) => withOcc(db, family.id, id, { takenBy: viewer.id, sync: 'pending' }));
      toast.success(`${pick(copy.heroToasts)} Naloga je tvoja samo za ta termin.`);
    },
    [occOf, viewer.id, setDb, family.id]
  );

  const declineOffer = useCallback(
    (id: string) => {
      const o = occOf(id);
      if (!o.offer || o.offer.toId !== viewer.id || o.takenBy) return;
      setDb((db) => withOcc(db, family.id, id, { offer: undefined, sync: 'pending' }));
      toast('Zavrnjeno. Naloga ostane pri prvotnem nosilcu.');
    },
    [occOf, viewer.id, setDb, family.id]
  );

  return { checkTrust, uncheck, submitForReview, markShared, approve, requestCorrection, offer, cancelOffer, takeOffer, declineOffer };
}
